import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useState,
  VideoHTMLAttributes
} from 'react'
// import mergeRefs from 'merge-refs'
import NextImage, { ImageProps as NextImageProps } from 'next/image'

import useMedia from '@/hooks/useMedia'

// import LazyVideo from '@/components/base/lazy-video'
import { cn } from '@/lib/utils'
import { WPImage, WPVideo } from '@/types'

export type MediaElement = {
  use_video: boolean
  is_mobile_video_needed: boolean
  image: Partial<WPImage>
  video: Partial<WPVideo>
  video_mobile: Partial<WPVideo>
  video_poster: Partial<WPImage>
}

export type ImageProps = Omit<NextImageProps, 'src' | 'alt'> &
  Partial<{
    source: Partial<WPImage>
    size: 'thumbnail' | 'medium' | 'large' | 'full'
    fit: 'contain' | 'cover'
    alt: string
  }>

export type VideoProps = VideoHTMLAttributes<HTMLVideoElement> &
  Partial<{
    source: Partial<WPVideo>
    source_mobile: Partial<WPVideo>
    fit: 'contain' | 'cover'
    fill: boolean
    is_mobile_video_needed: boolean
  }>

export type MediaProps = (ImageProps | VideoProps) & Partial<MediaElement>

const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    { source, size = 'full', fit = 'cover', fill, alt, className, ...props },
    ref
  ) => {
    const [loaded, setLoaded] = useState(false)
    const [src, width, height] = useMemo(
      () => [
        source?.sizes?.[size]?.url || source?.url || '',
        source?.sizes?.[size]?.width || source?.width || 0,
        source?.sizes?.[size]?.height || source?.height || 0
      ],
      [source, size]
    )

    return (
      <NextImage
        ref={ref}
        src={src}
        fill={fill}
        alt={alt || ''}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        draggable={false}
        className={cn(
          'transition-opacity duration-500 ease-in-out-cubic',
          {
            'object-cover object-center': fit === 'cover',
            'object-contain object-center': fit === 'contain'
          },
          {
            'opacity-0': !loaded,
            'opacity-100': loaded
          },
          className
        )}
        {...(!fill ? { width, height } : {})}
        {...props}
      />
    )
  }
)

const Video = forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      source,
      source_mobile,
      fit = 'cover',
      fill,
      className,
      is_mobile_video_needed,
      ...props
    },
    forwardRef
  ) => {
    const isMobile = useMedia('only screen and (max-width: 768px)', true)
    const [shouldRender, setShouldRender] = useState(
      is_mobile_video_needed ? false : true
    )

    useEffect(() => {
      if (is_mobile_video_needed) setShouldRender(true)
    }, [])

    if (!shouldRender && is_mobile_video_needed) return null

    return (
      <video
        ref={forwardRef}
        playsInline={true}
        autoPlay={true}
        loop={true}
        muted={true}
        className={cn(
          'relative',
          {
            'absolute left-0 top-0 h-full w-full': fill
          },
          {
            'object-cover object-center': fit === 'cover',
            'object-contain object-center': fit === 'contain'
          },
          className
        )}
        {...props}
      >
        <source
          src={
            isMobile && is_mobile_video_needed
              ? source_mobile?.url
              : source?.url
          }
          type="video/mp4"
        />
      </video>
    )
  }
)

const Media = forwardRef<HTMLImageElement | HTMLVideoElement, MediaProps>(
  (
    {
      use_video,
      is_mobile_video_needed,
      video_mobile,
      image,
      video,
      video_poster,
      ...props
    },
    ref
  ) => {
    return use_video ? (
      <Video
        ref={ref as ForwardedRef<HTMLVideoElement>}
        is_mobile_video_needed={is_mobile_video_needed}
        source_mobile={video_mobile}
        source={video}
        poster={video_poster?.url}
        {...(props as VideoProps)}
      />
    ) : (
      <Image
        ref={ref as ForwardedRef<HTMLImageElement>}
        source={image}
        alt={image?.alt}
        {...(props as ImageProps)}
      />
    )
  }
)

export default Media
